/**
 * Skelette -- die Form dessen, was gleich kommt.
 *
 * Ein kreisender Strich sagt "warte". Ein Skelett sagt "hier kommt eine
 * Tabelle mit sechs Spalten" -- und reserviert dabei den Platz, sodass die
 * Seite beim Eintreffen der Daten nicht springt. Das ist der Grund, warum es
 * sie gibt; das ruhigere Bild ist die Zugabe.
 *
 * **Fuer vorlesende Programme sind sie unsichtbar.** Sie tragen keine
 * Bedeutung, die man vorlesen koennte. Angesagt wird der Ladezustand einmal
 * vom umgebenden Bereich -- ``aria-busy`` an der Karte, nicht dreissig
 * Platzhalter, die einzeln "Grafik" melden.
 */

interface SkeletonTextProps {
  /** Wie viele Zeilen. Die letzte ist kuerzer -- so sieht Text aus. */
  lines?: number;
}

/** Ein Absatz aus Platzhalterzeilen. */
export function SkeletonText({ lines = 3 }: SkeletonTextProps) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton skeleton__line"
          style={{ width: index === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

interface SkeletonTableProps {
  /** So viele Spalten, wie die echte Tabelle hat. */
  columns: number;
  /** So viele Zeilen, wie ueblicherweise sichtbar sind. */
  rows?: number;
}

/**
 * Eine Tabelle aus Platzhalterzeilen.
 *
 * Die Zeilenzahl ist bewusst fest und nicht die spaetere Trefferzahl: Die
 * kennt niemand, bevor die Antwort da ist. Acht Zeilen fuellen den sichtbaren
 * Bereich, ohne eine Bildlaufleiste zu erzeugen, die gleich darauf wieder
 * verschwindet.
 */
export function SkeletonTable({ columns, rows = 8 }: SkeletonTableProps) {
  return (
    <div className="table-wrapper" aria-hidden="true">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => (
            <tr key={rowIndex} className="skeleton-row">
              {Array.from({ length: columns }, (_, columnIndex) => (
                <td key={columnIndex}>
                  {/* Ungleiche Breiten: Gleich lange Balken sehen aus wie ein
                      Muster, ungleiche wie Inhalt. Der Wert haengt an der
                      Position und nicht am Zufall -- sonst zappelte das
                      Skelett bei jedem neuen Darstellungslauf. */}
                  <div
                    className="skeleton skeleton__line"
                    style={{ width: `${String(50 + ((rowIndex * 7 + columnIndex * 13) % 45))}%` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SkeletonBlockProps {
  /** Hoehe der Flaeche, als CSS-Laenge. */
  height: string;
}

/** Eine rechteckige Flaeche -- fuer ein Diagramm oder ein Bild. */
export function SkeletonBlock({ height }: SkeletonBlockProps) {
  return <div className="skeleton" style={{ height }} aria-hidden="true" />;
}
